// Page-owned default filters for the task views. These replace the four selects
// the legacy ListingFilters bar used to render; everything else now comes from
// the user's own advanced filters.
//
// Values match what the backend advertises for entity "task":
//   status      enum     OPEN | IN_PROGRESS | INCOMPLETE | DONE | CANCELED
//   is_archived boolean  "true" | "false"
const IS_ARCHIVED_FALSE = { fieldName: "is_archived", filterType: "equal", filterValues: "false", editable: true, visible: true }

export const FOR_USER_FIELD = "for_user"

// TaskRabbit.board is a plain FK, so the backend advertises it as an ordinary
// relation field -- there is no dedicated argument behind it any more.
export const BOARD_FIELD = "board"

// "Work still in front of you" -- the default on My Tasks and on every board.
// Unlike for_user the user was always free to change or remove this one, so it
// carries editable: true (an x, not a lock icon) same as is_archived.
//
// It only bites in list view. A kanban grouped by status strips it and hides its
// chip (see stripGroupedFilterForKanban below), because there the columns ARE the
// statuses; a kanban grouped by a custom column keeps it, where it still means
// something.
const DEFAULT_OPEN_STATUSES = { fieldName: "status", filterType: "in", filterValues: ["OPEN", "IN_PROGRESS"], editable: true, visible: true }

export const BOARD_PRESETS = [IS_ARCHIVED_FALSE, DEFAULT_OPEN_STATUSES]

// The Driver and Incident task tabs have no board id to route on. They are task
// lists scoped to one record rather than boards, and they show every task that
// belongs to it -- so they take only the "hide archived" default, not the status
// one. Kept as its own export so a change to the board defaults cannot silently
// reach into those two tabs.
export const TASK_TAB_PRESETS = [IS_ARCHIVED_FALSE]

// "tasks" is the route segment My Tasks uses in place of a board id.
//
// for_user is what makes My Tasks "mine" -- it is the definition of the page
// rather than a filter anyone would change, so it carries `visible: false` and
// shows up nowhere: no chip, no row in the popup, not in the count. It still
// applies: combineFilters ignores `visible`, and AdvancedFilters' Apply carries
// invisible presets through untouched. On a board view it is absent from the
// presets entirely and freely selectable in the popup.
export const getTaskFilterPresets = (boardId, currentUserId) =>
    (!boardId || boardId === "tasks")
        ? [
            IS_ARCHIVED_FALSE,
            DEFAULT_OPEN_STATUSES,
            { fieldName: FOR_USER_FIELD, filterType: "equal", filterValues: currentUserId, editable: false, visible: false },
        ]
        : BOARD_PRESETS

// The board a view is pinned to replaces the boardId/boardIds arguments allTasks
// used to take. It is structure rather than a user choice -- the route already
// decided it -- so it is composed onto the query at the leaf, the same way the
// kanban's status column and the My Tasks date groups are, and never reaches the
// URL, the chips, or the saved filters.
//
// My Tasks ("tasks", or no board id at all) spans every board and adds nothing.
// So do the Driver and Incident task tabs, which scope by record instead.
export const boardScopeFilters = (boardId) =>
    (!boardId || boardId === "tasks")
        ? []
        : [{ fieldName: BOARD_FIELD, filterType: "equal", filterValues: boardId }]

// A kanban's columns ARE the values of whatever it groups by, so a filter on
// that same field can only fight them -- it empties every column but one.
//
// groupBy is either "status" or a custom ENUM column's pk (see
// KanbanSettingsButton). The filter system names custom columns
// custom_column_<pk>, the same convention the table uses for its column ids.
export const kanbanGroupedFieldName = (viewType, groupBy) => {
    if (viewType !== "kanban" || !groupBy) return null
    return groupBy === "status" ? "status" : `custom_column_${groupBy}`
}

// Everything the Advanced Filters popup must not offer on this view: the kanban's
// grouping field, plus for_user on My Tasks, where the page already pins it to the
// current user (see the invisible preset below) and picking it could only restate
// or contradict that.
//
// `board` is the mirror image: on a board view the route pins it (see
// boardScopeFilters), so offering it could only restate that or -- picking any
// other board -- AND itself into an empty list. On My Tasks it stays available,
// where narrowing to one board is a real thing to want.
export const getHiddenFilterFields = (boardId, viewType, groupBy) => {
    const hidden = []
    const groupedField = kanbanGroupedFieldName(viewType, groupBy)
    if (groupedField) hidden.push(groupedField)
    if (!boardId || boardId === "tasks") hidden.push(FOR_USER_FIELD)
    else hidden.push(BOARD_FIELD)
    return hidden.length ? hidden : undefined
}

// Dropped from the query only -- the filter stays in the URL and applies again
// in list view, or when the board is grouped by something else. The chip is
// hidden alongside it (the header passes the same field to
// AppliedAdvancedFilters) because a chip you cannot act on and that changes
// nothing is just noise.
export const stripGroupedFilterForKanban = (filters, viewType, groupBy) => {
    const groupedField = kanbanGroupedFieldName(viewType, groupBy)
    if (!groupedField) return filters || []
    return (filters || []).filter((filter) => filter?.fieldName !== groupedField)
}
